import { CircularProgress, LinearProgress, Typography } from '@material-ui/core';
import classnames from 'classnames';
import { FC, ReactNode } from 'react';
import { useDropzone } from 'react-dropzone';
import { ReactComponent as UploadSimple } from '../../../../../Asset/svg/UploadSimple.svg';
import {
  VideoModalActions,
  VideoModalCheckboxes,
  VideoModalCheckboxState,
  VideoModalHeader,
} from './VideoModalShared';
import { useVideoImportStyles } from './VideoImport.styles';
import { formatMaxUploadMo } from './utils';

type VideoImportUploadModalProps = VideoModalCheckboxState & {
  onClose: () => void;
  onBack: () => void;
  videoFile: File | null;
  uploadedVideoUrl: string;
  uploadProgress: number;
  onVideoFileChange: (file: File | null) => void;
  onDeleteUploadedVideo: () => void;
  uploadError: string;
  onUploadError: (message: string) => void;
  maxUploadBytes: number;
  isUploading: boolean;
  onValidate: () => void;
  tabs?: ReactNode;
  showHeader?: boolean;
  submitLabel?: ReactNode;
  validateDisabled?: boolean;
  fileInputLabel?: string;
  previewTitle?: string;
};

const VideoImportUploadModal: FC<VideoImportUploadModalProps> = ({
  onClose,
  onBack,
  videoFile,
  uploadedVideoUrl,
  uploadProgress,
  onVideoFileChange,
  onDeleteUploadedVideo,
  uploadError,
  onUploadError,
  maxUploadBytes,
  isUploading,
  onValidate,
  tabs,
  showHeader = true,
  submitLabel,
  validateDisabled,
  fileInputLabel = 'Fichier vidéo',
  previewTitle = 'Vidéo importée',
  ...checkboxState
}) => {
  const classes = useVideoImportStyles();
  const maxUploadLabel = formatMaxUploadMo(maxUploadBytes);
  const { getRootProps, getInputProps } = useDropzone({
    accept: 'video/mp4,video/quicktime,video/x-msvideo,.mp4,.mov,.avi',
    maxSize: maxUploadBytes,
    noKeyboard: true,
    disabled: isUploading,
    onDrop: acceptedFiles => {
      onUploadError('');
      if (acceptedFiles[0]) {
        onVideoFileChange(acceptedFiles[0]);
      }
    },
    onDropRejected: () => {
      onUploadError(`Format non accepté ou fichier supérieur à ${maxUploadLabel}.`);
    },
  });

  return (
    <div className={classes.videoModalStep}>
      {showHeader && <VideoModalHeader title="Uploader une vidéo" onClose={onClose} />}
      {tabs}
      <div className={classes.videoModalForm}>
        {uploadedVideoUrl ? (
          <div className={classes.videoUploadPreviewBox}>
            <div className={classes.videoUploadPreviewHeader}>
              <Typography className={classes.videoUploadPreviewTitle}>{previewTitle}</Typography>
              <button
                type="button"
                aria-label="Supprimer la vidéo importée"
                className={classes.videoUploadDeleteBtn}
                onClick={onDeleteUploadedVideo}>
                Supprimer
              </button>
            </div>
            <video
              key={uploadedVideoUrl}
              className={classes.videoUploadPreviewPlayer}
              src={uploadedVideoUrl}
              controls={true}>
              <track kind="captions" />
            </video>
          </div>
        ) : (
          <div className={classes.videoUploadZoneWrap}>
            <div
              {...getRootProps({
                className: classnames(classes.videoUploadZone, {
                  [classes.videoUploadZoneDisabled]: isUploading,
                }),
              })}>
              <input {...getInputProps({ 'aria-label': fileInputLabel })} />
              <div className={classes.videoUploadZoneIconWrap}>
                <UploadSimple className={classes.videoModalCardIconUpload} aria-hidden={true} />
              </div>
              <div className={classes.videoUploadZoneTexts}>
                <Typography className={classes.videoUploadZoneTitle}>
                  {videoFile ? videoFile.name : 'Cliquez pour uploader une vidéo'}
                </Typography>
                <Typography className={classes.videoUploadZoneHint}>
                  MP4, MOV, AVI jusqu&apos;à {maxUploadLabel}
                </Typography>
              </div>
            </div>
            {isUploading && (
              <div className={classes.videoUploadProgressWrap}>
                <LinearProgress
                  variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
                  value={uploadProgress}
                  className={classes.videoUploadProgress}
                />
                <Typography className={classes.videoUploadProgressText}>
                  Téléversement en cours{uploadProgress > 0 ? ` ${uploadProgress}%` : ''}
                </Typography>
              </div>
            )}
          </div>
        )}
        {uploadError && (
          <Typography className={classes.videoUploadError}>{uploadError}</Typography>
        )}
        <VideoModalCheckboxes {...checkboxState} />
      </div>
      <VideoModalActions
        validateDisabled={validateDisabled ?? (!uploadedVideoUrl || isUploading)}
        validateLabel={
          isUploading ? (
            <CircularProgress size={22} className={classes.videoModalValidateSpinner} />
          ) : (
            submitLabel || 'Valider'
          )
        }
        onValidate={onValidate}
        onBack={onBack}
      />
    </div>
  );
};

export default VideoImportUploadModal;
